<template>
  <div class="min-h-screen px-4 pb-16 pt-28 md:px-8">
    <div class="mx-auto flex w-full max-w-5xl flex-col gap-6">
      <div
        id="flappy-bird-game"
        ref="gameArea"
        class="panel tech-grid relative mx-auto h-[60vh] max-h-[720px] w-full overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--panel)] outline-none"
        @keydown.space.prevent="handleAction"
        @pointerdown.self="handleAction"
        @mouseenter="focusGame"
        tabindex="0"
      >
        <div class="absolute inset-0 bg-gradient-to-b from-[rgba(182,255,90,0.08)] via-transparent to-[rgba(15,23,34,0.9)]" />
        <div class="absolute left-4 top-4 z-10 flex flex-wrap gap-2">
          <div class="rounded-full border border-[var(--border)] bg-[var(--panel)] px-3 py-1 text-xs text-[var(--text-secondary)]">
            {{ $t('flappy.score') }}: <span class="text-[var(--accent)]">{{ score }}</span>
          </div>
          <div class="rounded-full border border-[var(--border)] bg-[var(--panel)] px-3 py-1 text-xs text-[var(--text-secondary)]">
            {{ $t('flappy.best') }}: <span class="text-[var(--accent)]">{{ bestScore }}</span>
          </div>
        </div>
        <div
          class="absolute bg-no-repeat bg-center bg-contain bird"
          :style="{
            top: birdY + 'px',
            left: birdX + 'px',
            width: birdSize + 'px',
            height: birdSize + 'px',
            backgroundSize: '120% 100%',
            backgroundImage: `url(${require('../assets/flappyBird1.png')})`
          }"
        ></div>
        <div class="pipes">
          <template v-for="pipe in pipes" :key="pipe.id">
            <div
              class="absolute border border-[var(--accent)] bg-[rgba(182,255,90,0.2)] pipe"
              :style="{ height: pipe.gapTop + 'px', left: pipe.left + 'px', top: 0, width: pipeWidth + 'px' }"
            ></div>
            <div
              class="absolute border border-[var(--accent)] bg-[rgba(182,255,90,0.2)] pipe"
              :style="{ height: pipe.bottomHeight + 'px', left: pipe.left + 'px', top: pipe.gapTop + gapSize + 'px', width: pipeWidth + 'px' }"
            ></div>
          </template>
        </div>
        <div
          v-if="gameState !== 'running'"
          class="absolute inset-0 z-20 flex items-center justify-center bg-[rgba(5,7,9,0.6)]"
        >
          <div class="panel w-[90%] max-w-md p-6 text-center">
            <p class="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">
              {{ stateLabel }}
            </p>
            <h2 class="mt-3 text-2xl font-bold text-[var(--text-primary)]">
              {{ stateTitle }}
            </h2>
            <p class="mt-2 text-sm text-[var(--text-muted)]">
              {{ stateSubtitle }}
            </p>
            <div class="mt-5 flex flex-wrap justify-center gap-3">
              <button v-if="gameState === 'paused'" class="btn-primary" @click="resumeGame">
                {{ $t('flappy.resume') }}
              </button>
              <button v-if="gameState !== 'paused'" class="btn-primary" @click="startGame">
                {{ gameState === 'over' ? $t('flappy.restart') : $t('flappy.start') }}
              </button>
              <button class="btn-secondary" @click="goBack">
                {{ $t('flappy.back') }}
              </button>
            </div>
            <p class="mt-4 text-xs text-[var(--text-muted)]">
              {{ $t('flappy.hint') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      gameState: 'idle',
      score: 0,
      bestScore: 0,
      birdY: 0,
      birdX: 0,
      birdSize: 42,
      birdVelocity: 0,
      pipes: [],
      pipeWidth: 44,
      gapSize: 150,
      pipeSpacing: 240,
      pipeSpeed: 2.4,
      gravity: 0.35,
      lift: -6.5,
      gameWidth: 0,
      gameHeight: 0,
      animationFrameId: null,
      lastFrameTime: 0,
      pipeSeed: 0,
      audioContext: null,
    };
  },
  computed: {
    stateLabel() {
      if (this.gameState === 'paused') {
        return this.$t('flappy.pausedLabel');
      }
      if (this.gameState === 'over') {
        return this.$t('flappy.gameOverLabel');
      }
      return this.$t('flappy.readyLabel');
    },
    stateTitle() {
      if (this.gameState === 'paused') {
        return this.$t('flappy.pausedTitle');
      }
      if (this.gameState === 'over') {
        return this.$t('flappy.gameOverTitle');
      }
      return this.$t('flappy.readyTitle');
    },
    stateSubtitle() {
      if (this.gameState === 'paused') {
        return this.$t('flappy.pausedSubtitle');
      }
      if (this.gameState === 'over') {
        return this.$t('flappy.gameOverSubtitle');
      }
      return this.$t('flappy.readySubtitle');
    },
  },
  methods: {
    goBack() {
      const from = this.$route.query.from;
      if (from) {
        this.$router.push(from);
        return;
      }
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.push('/');
    },
    handleAction() {
      this.focusGame();
      if (this.gameState === 'running') {
        this.jump();
        return;
      }
      if (this.gameState === 'paused') {
        this.resumeGame();
        return;
      }
      this.startGame();
    },
    startGame() {
      if (this.gameState === 'running') {
        return;
      }
      this.resetGame();
      this.gameState = 'running';
      this.lastFrameTime = 0;
      this.animationFrameId = requestAnimationFrame((timestamp) => this.tick(timestamp));
    },
    focusGame() {
      if (this.$refs.gameArea) {
        this.$refs.gameArea.focus();
      }
    },
    pauseGame() {
      if (this.gameState !== 'running') {
        return;
      }
      this.gameState = 'paused';
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    },
    resumeGame() {
      if (this.gameState !== 'paused') {
        return;
      }
      this.gameState = 'running';
      this.lastFrameTime = 0;
      this.animationFrameId = requestAnimationFrame((timestamp) => this.tick(timestamp));
    },
    jump() {
      if (this.gameState !== 'running') {
        return;
      }
      this.birdVelocity = this.lift;
      this.playSound(520, 0.06);
      this.tryVibrate(10);
    },
    tick(timestamp) {
      if (this.gameState !== 'running') {
        return;
      }
      if (!this.lastFrameTime) {
        this.lastFrameTime = timestamp;
      }
      const delta = (timestamp - this.lastFrameTime) / 16.67;
      this.lastFrameTime = timestamp;
      this.update(delta);
      this.animationFrameId = requestAnimationFrame((nextTimestamp) => this.tick(nextTimestamp));
    },
    update(delta) {
      this.birdVelocity += this.gravity * delta;
      this.birdY += this.birdVelocity * delta;

      if (this.birdY < 0) {
        this.birdY = 0;
        this.birdVelocity = 0;
      }

      if (this.birdY + this.birdSize >= this.gameHeight) {
        this.triggerGameOver();
        return;
      }

      this.updatePipes(delta);
      this.checkCollision();
    },
    updatePipes(delta) {
      const speed = this.pipeSpeed * delta;
      for (let i = 0; i < this.pipes.length; i++) {
        const pipe = this.pipes[i];
        pipe.left -= speed;
        if (!pipe.passed && pipe.left + this.pipeWidth < this.birdX) {
          pipe.passed = true;
          this.score += 1;
          this.playSound(760, 0.05);
        }
      }

      this.pipes = this.pipes.filter((pipe) => pipe.left + this.pipeWidth > 0);

      const lastPipe = this.pipes[this.pipes.length - 1];
      if (!lastPipe || lastPipe.left < this.gameWidth - this.pipeSpacing) {
        this.spawnPipe(this.gameWidth + this.pipeSpacing);
      }
    },
    checkCollision() {
      const birdLeft = this.birdX;
      const birdRight = this.birdX + this.birdSize;
      const birdTop = this.birdY;
      const birdBottom = this.birdY + this.birdSize;

      for (let i = 0; i < this.pipes.length; i++) {
        const pipe = this.pipes[i];
        const pipeRight = pipe.left + this.pipeWidth;
        if (birdRight < pipe.left || birdLeft > pipeRight) {
          continue;
        }

        const gapTop = pipe.gapTop;
        const gapBottom = pipe.gapTop + this.gapSize;
        if (birdTop < gapTop || birdBottom > gapBottom) {
          this.triggerGameOver();
          return;
        }
      }
    },
    spawnPipe(offset) {
      const minHeight = Math.max(40, this.gameHeight * 0.12);
      const maxHeight = Math.max(minHeight + 40, this.gameHeight * 0.6);
      const gapTop = Math.floor(Math.random() * (maxHeight - minHeight)) + minHeight;
      const bottomHeight = Math.max(20, this.gameHeight - gapTop - this.gapSize);

      this.pipes.push({
        id: `pipe-${this.pipeSeed}`,
        left: offset,
        gapTop,
        bottomHeight,
        passed: false,
      });
      this.pipeSeed += 1;
    },
    resetGame() {
      this.setDimensions();
      this.score = 0;
      this.birdVelocity = 0;
      this.birdY = this.gameHeight * 0.4;
      this.pipes = [];
      this.pipeSeed = 0;
      this.spawnPipe(this.gameWidth + this.pipeSpacing * 0.5);
      this.spawnPipe(this.gameWidth + this.pipeSpacing * 1.5);
    },
    setDimensions() {
      if (!this.$refs.gameArea) {
        return;
      }
      const rect = this.$refs.gameArea.getBoundingClientRect();
      this.gameWidth = rect.width;
      this.gameHeight = rect.height;
      const scale = Math.min(Math.max(rect.width / 600, 0.7), 1.1);
      this.birdSize = Math.round(42 * scale);
      this.pipeWidth = Math.round(44 * scale);
      this.gapSize = Math.round(150 * scale);
      this.pipeSpacing = Math.round(240 * scale);
      this.pipeSpeed = 2.4 * scale;
      this.gravity = 0.35 * scale;
      this.lift = -6.5 * scale;
      this.birdX = Math.round(rect.width * 0.22);
    },
    triggerGameOver() {
      if (this.gameState !== 'running') {
        return;
      }
      this.gameState = 'over';
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      this.bestScore = Math.max(this.bestScore, this.score);
      localStorage.setItem('flappyBestScore', String(this.bestScore));
      this.playSound(220, 0.12);
      this.tryVibrate(60);
    },
    onKeyDown(event) {
      if (event.code !== 'Space') {
        return;
      }
      event.preventDefault();
      this.handleAction();
    },
    playSound(frequency, duration) {
      try {
        if (!this.audioContext) {
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        gainNode.gain.value = 0.08;
        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        oscillator.start();
        oscillator.stop(this.audioContext.currentTime + duration);
      } catch (error) {
        // Audio context may fail on restricted devices.
      }
    },
    tryVibrate(duration) {
      if (navigator.vibrate) {
        navigator.vibrate(duration);
      }
    },
  },
  mounted() {
    const savedBest = Number(localStorage.getItem('flappyBestScore'));
    this.bestScore = Number.isNaN(savedBest) ? 0 : savedBest;
    this.setDimensions();
    this.resetGame();
    window.addEventListener('resize', this.setDimensions);
    window.addEventListener('keydown', this.onKeyDown);
    this.focusGame();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.setDimensions);
    window.removeEventListener('keydown', this.onKeyDown);
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  },
};
</script>

<style scoped>

.bird {
  position: absolute;
  width: 50px;
  height: 50px;
}

.pipe {
  position: absolute;
  width: 50px;
  background-color: green;
}

@media screen and (max-width: 600px) {
  .bird {
    width: 35px !important;
    height: 35px !important;
  }
  .pipe {
    width: 35px !important;
  }
}
</style>
